/** Appearance applied to the host element: accent → CSS vars, theme/position → data attributes. */
export interface Appearance {
  accent?: string | undefined;
  theme?: "auto" | "light" | "dark" | undefined;
  position?: "right" | "left" | undefined;
  launcherLabel?: string | undefined;
  poweredBy?: boolean | undefined;
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseHex(color: string | undefined): [number, number, number] | undefined {
  if (!color) return undefined;
  const m = HEX.exec(color.trim());
  if (!m) return undefined;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Black or white text for the given background (YIQ). */
export function inkFor(rgb: [number, number, number]): string {
  const [r, g, b] = rgb;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 150 ? "#111318" : "#ffffff";
}

/** Attribute overrides win over stored appearance; host CSS `--workky-accent` wins over both when set. */
export function applyAppearance(host: HTMLElement, appearance: Appearance, attrs: Appearance): void {
  const a = { ...appearance, ...stripUndefined(attrs) };
  const hostAccent = getComputedStyle(host).getPropertyValue("--workky-accent").trim();
  const rgb = parseHex(hostAccent) ?? parseHex(a.accent);
  if (rgb) {
    host.style.setProperty("--wk-accent", `rgb(${rgb.join(",")})`);
    host.style.setProperty("--wk-accent-rgb", rgb.join(","));
    host.style.setProperty("--wk-accent-ink", inkFor(rgb));
  }
  if (a.theme && a.theme !== "auto") host.dataset.theme = a.theme;
  else delete host.dataset.theme;
  if (a.position === "left") host.dataset.position = "left";
  else delete host.dataset.position;
}

export function attrsAppearance(ds: DOMStringMap): Appearance {
  const theme = ds.workkyTheme;
  const position = ds.workkyPosition;
  return {
    accent: ds.workkyAccent,
    theme: theme === "light" || theme === "dark" || theme === "auto" ? theme : undefined,
    position: position === "left" || position === "right" ? position : undefined,
    launcherLabel: ds.workkyLabel,
  };
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}
